import { Injectable, inject } from '@angular/core';
import { OktaAuth } from '@okta/okta-auth-js';
import { UserProfileService } from './user-profile.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly oktaAuth = inject(OktaAuth);
  private readonly userProfileService = inject(UserProfileService);

  // Expose user profile observable from user profile service
  public readonly userProfile$ = this.userProfileService.userProfile$;

  async loadUserProfile(userClaims: any): Promise<void> {
    this.userProfileService.loadUserProfile(userClaims);
  }

  async loadUserProfileOptimized(): Promise<boolean> {
    try {
      // First check if user is actually authenticated
      const isAuthenticated = await this.oktaAuth.isAuthenticated();
      if (!isAuthenticated) {
        this.userProfileService.clearUserProfile();
        return false;
      }
      // Check for cached user data first to avoid unnecessary API calls
      const cookieValue = this.userProfileService.getUserFromCookie();

      if (cookieValue !== null && cookieValue !== undefined && cookieValue !== '') {
        // Validate that cached data is still valid by checking token
        try {
          // Quick token validation - if this fails, cache is stale
          await this.oktaAuth.tokenManager.get('accessToken');
          this.userProfileService.loadUserProfile(cookieValue);
          return true;
        } catch (tokenError) {
          this.userProfileService.clearUserProfile();
          // Fall through to fetch fresh user data
        }
      }
      // Fetch fresh user data from OKTA
      try {
        const userClaims = await this.oktaAuth.getUser();
        this.userProfileService.loadUserProfile(userClaims);
        return true;
      } catch (userError: any) {
        // If token is revoked or unauthorized, clear everything and redirect to login
        if (userError.message?.includes('revoked') || userError.message?.includes('401') || userError.message?.includes('Unauthorized')) {
          this.userProfileService.clearUserProfile();
          this.oktaAuth.tokenManager.clear();
          return false;
        }
        throw userError; // Re-throw other errors
      }
    } catch (error: any) {
      // Handle revoked/unauthorized tokens specifically
      if (error.message?.includes('revoked') || error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        this.userProfileService.clearUserProfile();
        this.oktaAuth.tokenManager.clear();
        // Redirect to login page instead of staying on current page
        setTimeout(() => {
          window.location.href = window.location.origin;
        }, 100);
        return false;
      }
      // For other errors, clear stale data but don't force redirect
      this.userProfileService.clearUserProfile();
      this.oktaAuth.tokenManager.clear();
      return false;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      return await this.oktaAuth.isAuthenticated();
    } catch (error: any) {
      // If there's an error checking authentication, assume not authenticated
      return false;
    }
  }

  async signIn(): Promise<void> {
    await this.oktaAuth.signInWithRedirect();
  }

  async logout() {
    await this.oktaAuth.signOut();
    document.cookie = "UserDetails=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/";
  }

  getUserFromCookie(): any {
    return this.userProfileService.getUserFromCookie();
  }

  async getAccessToken(): Promise<string | undefined> {
    try {
      return await this.oktaAuth.getAccessToken();
    } catch (error) {
      return undefined;
    }
  }

  async getIdToken(): Promise<string | undefined> {
    try {
      return await this.oktaAuth.getIdToken();
    } catch (error) {
      return undefined;
    }
  }
}
